import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import React from "react";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";

const ViewOpportunityDrawer = ({ selectedLead, onClose, companyName, companyLogo, headquarters }) => {
	return (
		<AnimatePresence mode="wait">
			{selectedLead && (
				<>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3 }}
						className="fixed inset-0 z-40 bg-black/30"
						onClick={onClose}
						aria-hidden="true"
					/>
					<motion.div
						initial={{ x: "100%" }}
						animate={{ x: 0 }}
						exit={{ x: "100%" }}
						transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
						className="fixed inset-y-0 right-0 z-50 w-[700px] bg-white border-l border-gray-200 shadow-xl rounded-l-2xl overflow-hidden"
					>
						<div className="relative h-full flex flex-col">
							<div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
								<div className="flex items-center justify-between">
									<div className="flex items-center space-x-4">
										<div className="w-16 h-16 rounded-sm overflow-hidden">
											{companyLogo ? (
												<img
													alt="Company Logo"
													src={companyLogo}
													onError={(e) => (e.target.src = "/images/co-placeholder.png")}
												/>
											) : (
												<div className="bg-gray-100 h-full w-full" />
											)}
										</div>

										<div>
											<h2 className="text-lg font-bold text-gray-900">{companyName}</h2>
											<p className="text-gray-500">{headquarters}</p>
										</div>
									</div>
									<Button variant="outline" className="h-8 w-8 p-0" onClick={onClose}>
										<X className="h-4 w-4" />
									</Button>
								</div>
							</div>
							<div className="flex-grow overflow-hidden">
								<ScrollArea className="h-full">
									<div className="space-y-6 p-6">
										<section>
											<div className="flex items-center justify-between mb-3">
												<h3 className="font-medium text-gray-900">Lead Type</h3>
											</div>
											<Badge variant="open">{selectedLead?.emailType?.replace("_", " ")}</Badge>
										</section>
										<section>
											<div className="flex items-center justify-between mb-3">
												<h3 className="font-medium text-gray-900">Email Timeline & Status</h3>
											</div>
											<div className="space-y-2">
												<p className="text-sm text-gray-700">
													<span className="text-gray-500">Scheduled At:</span>{" "}
													{selectedLead?.scheduledAt &&
														dayjs(selectedLead?.scheduledAt?.$date).format("MMM DD YYYY HH:MM:ss")}
												</p>
												<p className="text-sm text-gray-700">
													<span className="text-gray-500">Send At:</span>{" "}
													{selectedLead?.sentAt && dayjs(selectedLead?.sentAt?.$date).format("MMM DD YYYY HH:MM:ss")}
												</p>
												<p className="text-sm text-gray-700">
													<span className="text-gray-500">Opened:</span>{" "}
													<Badge variant={selectedLead?.opened ? "open" : "default"}>
														{selectedLead?.opened ? "YES" : "NO"}
													</Badge>
												</p>
												<p className="text-sm text-gray-700">
													<span className="text-gray-500">Opened At:</span>{" "}
													{selectedLead?.openAt && dayjs(selectedLead?.openAt?.$date).format("MMM DD YYYY HH:MM:ss")}
												</p>
											</div>
										</section>
										<section>
											<div className="flex items-center justify-between mb-3">
												<h3 className="font-medium text-gray-900">Jobs</h3>
											</div>
											<div className="space-y-4">
												{["Marketing Manager", "Frontend Developer", "Backend Developer"].map((job, index) => {
													return (
														<div key={index} className="flex items-center cursor-pointer">
															<a href="/jobs/marketing-manager-at-lumac-limited-688797cd73778b19b24e80d2">
																<div className="flex items-center mb-1">
																	<h3 className="text-sm font-semibold mr-2 font-inter transition-colors duration-300 hover:text-primary-700 text-gray-900">
																		{job}
																	</h3>
																</div>
																<p className="text-[14px] text-gray-600 flex items-center flex-wrap">
																	<span className="flex items-center mr-3">
																		<svg
																			xmlns="http://www.w3.org/2000/svg"
																			fill="none"
																			viewBox="0 0 24 24"
																			strokeWidth="1.5"
																			stroke="currentColor"
																			className="w-4 h-4 mr-1"
																		>
																			<path
																				strokeLinecap="round"
																				strokeLinejoin="round"
																				d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
																			/>
																			<path
																				strokeLinecap="round"
																				strokeLinejoin="round"
																				d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
																			/>
																		</svg>
																		Dar es Salaam, Tanzania
																	</span>
																	<span className="flex items-center mr-3">
																		<svg
																			xmlns="http://www.w3.org/2000/svg"
																			fill="none"
																			viewBox="0 0 24 24"
																			strokeWidth="1.5"
																			stroke="currentColor"
																			className="w-4 h-4 mr-1"
																		>
																			<path
																				strokeLinecap="round"
																				strokeLinejoin="round"
																				d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
																			/>
																		</svg>
																		Aug 10, 2025
																	</span>
																</p>
															</a>
														</div>
													);
												})}
											</div>
										</section>
									</div>
								</ScrollArea>
							</div>
						</div>
					</motion.div>
				</>
			)}
		</AnimatePresence>
	);
};

export default ViewOpportunityDrawer;
